import {all, call, put, select, takeEvery, takeLatest} from 'redux-saga/effects';
import Api from "../services/api";
import {checkUserHelper} from "../services/api-util";
import {getJWT} from "../../common/util/util-auth";
import {appendOlderNotifications, fetchNotifications, notificationsLoadMoreFailed, setNotifications} from "../../common/package/notification/actions/notifications";
import {checkFeatureFlag} from "../../common/components/feature-flags";
import {loadPersistedNotifications, savePersistedNotifications} from "../../common/package/notification/notification-persistence";

const NOTIFICATION_PERSISTENCE_FLAG_KEY = "NotificationPersistenceON";
const PAGE_SIZE = 20;

// Phase 2 of the notifications project: syncs the client-only Phase 1 actions
// (MARK_NOTIFICATION_READ / CLEAR_NOTIFICATION / CLEAR_ALL_NOTIFICATIONS, already
// dispatched by the bell dropdown) to the notification service's REST API, and
// fetches history on login.
//
// checkUserHelper(getJWT()) rather than checkUserHelper(action.data.user): these
// actions carry no `user` payload (they're plain {type, id} objects dispatched from
// src/common/package/notification, not the generic resource-action shape
// checkUserHelper normally receives it from). checkUserHelper only actually reads
// `.access_token`/`.refresh_token` off whatever's passed in — the exact two fields
// getJWT() already returns from cookies — so this still gets the same
// proactive refresh-before-expiry guard every other saga in this app gets, instead of
// silently sending a stale token the way a bare getJWT() read would.
//
// Persistence is gated behind NOTIFICATION_PERSISTENCE_FLAG_KEY for a controlled
// rollout (the realtime-backend enforces the same gate server-side on its own writes,
// this just avoids firing REST calls the backend would silently no-op for a flagged-off
// user).
function isPersistenceFlagOn() {
    return checkFeatureFlag(NOTIFICATION_PERSISTENCE_FLAG_KEY);
}

function* currentUser() {
    return yield* checkUserHelper(getJWT());
}

export function* fetchNotificationsCall() {
    // Flagged-off users have no backend history to fetch: fall back to whatever this
    // browser last saved locally, restoring the pre-Phase-2 "survives a refresh" behavior.
    if (!isPersistenceFlagOn()) {
        const {list} = loadPersistedNotifications();
        yield put(setNotifications({
            unreadCount: list.filter((it) => !it.IsRead).length,
            notifications: {list, count: list.length},
            nextCursor: null,
        }));
        return;
    }

    const user = yield* currentUser();
    if (!user?.access_token) {
        return;
    }

    // ADD_NOTIFICATION (a live socket push, notificationMiddleware.jsx) can land in Redux
    // state while the two REST calls below are in flight - it's dispatched independently
    // of this saga (a separate takeEvery watcher, watchNotificationSync below), so nothing
    // blocks it from racing ahead. Snapshot the list's IDs before firing the requests so
    // any item that shows up afterward without being one we already knew about, and
    // without being part of the fetched snapshot, can be identified as "arrived mid-fetch"
    // rather than either "already known" or "part of this fetch's own result".
    const idsBeforeFetch = new Set(
        (yield select((state) => state.notifications?.data?.notifications?.list ?? []))
            .map((it) => it.ID)
    );

    const [listResult, countResult] = yield all([
        call(Api.getNotifications, user, PAGE_SIZE),
        call(Api.getUnreadNotificationCount, user),
    ]);

    if (listResult?.status === 0) {
        // The backend returns newest-first (DynamoDB ScanIndexForward: false), but this
        // app's Redux state stores the list oldest-first (ascending) everywhere else - a
        // live push appends to the end (notificationMiddleware.jsx), and the bell dropdown
        // reverses once at render time to show newest-first. Reverse here too so a
        // REST-fetched list matches that same convention instead of ending up upside down.
        const fetchedList = [...(listResult.data?.notifications ?? [])].reverse();
        const fetchedIds = new Set(fetchedList.map((it) => it.ID));

        // Anything in state right now that was neither in the pre-fetch snapshot nor
        // returned by this fetch must have been pushed live during the fetch window -
        // preserve it (appended at the end, since it's the newest) instead of letting the
        // unconditional SET_NOTIFICATIONS below silently drop it. Anything that was in the
        // pre-fetch snapshot but is simply missing from the fetched result was removed
        // server-side (read/deleted elsewhere) and must stay dropped, not be resurrected.
        const currentList = yield select((state) => state.notifications?.data?.notifications?.list ?? []);
        const racedIn = currentList.filter((it) => !idsBeforeFetch.has(it.ID) && !fetchedIds.has(it.ID));

        const list = [...fetchedList, ...racedIn];
        const baseUnreadCount = countResult?.status === 0
            ? (countResult.data?.count ?? 0)
            : fetchedList.filter((it) => !it.IsRead).length;
        const unreadCount = baseUnreadCount + racedIn.filter((it) => !it.IsRead).length;

        yield put(setNotifications({
            unreadCount,
            notifications: {list, count: list.length},
            nextCursor: listResult.data?.nextCursor ?? null,
        }));
    } else {
        console.error("fetchNotificationsCall: failed to fetch notifications", listResult);
    }
}

export function* fetchMoreNotificationsCall() {
    // Lazy-load is scoped to the backend-persisted path only (see design spec) - the
    // flag-off localStorage fallback already loads its whole (locally-capped) list in
    // one shot via fetchNotificationsCall, so there's nothing to page through here.
    if (!isPersistenceFlagOn()) {
        return;
    }

    const cursor = yield select((state) => state.notifications?.data?.nextCursor ?? null);
    if (!cursor) {
        return;
    }

    const user = yield* currentUser();
    if (!user?.access_token) {
        // Unlike every other call in this file, this dispatches a failure signal rather
        // than a bare return: fetchMoreNotificationsCall is the only one with a spinner
        // (isLoadingMore) that needs clearing on this path.
        yield put(notificationsLoadMoreFailed());
        return;
    }

    const result = yield call(Api.getNotifications, user, PAGE_SIZE, cursor);

    // A concurrent full resync (e.g. FETCH_NOTIFICATIONS after a failed mutation - see
    // markNotificationReadCall/clearNotificationCall/clearAllNotificationsCall) replaces
    // the entire list and cursor via SET_NOTIFICATIONS while this fetch was in flight.
    // Appending this now-stale older page onto that fresh list would silently reintroduce
    // a gap and orphan the new cursor - bail instead of blindly appending.
    const cursorAfter = yield select((state) => state.notifications?.data?.nextCursor ?? null);
    if (cursorAfter !== cursor) {
        yield put(notificationsLoadMoreFailed());
        return;
    }

    if (result?.status === 0) {
        // Same newest-first -> oldest-first reversal as the initial fetch; "load more"
        // pages are older history, so they get prepended to the front of the list by
        // the reducer's APPEND_OLDER_NOTIFICATIONS case, not appended.
        const list = [...(result.data?.notifications ?? [])].reverse();
        yield put(appendOlderNotifications({
            list,
            nextCursor: result.data?.nextCursor ?? null,
        }));
    } else {
        console.error("fetchMoreNotificationsCall: failed to fetch more notifications", result);
        yield put(notificationsLoadMoreFailed());
    }
}

export function* markNotificationReadCall(action) {
    if (!isPersistenceFlagOn()) {
        // Reads state after the reducer already applied this action (redux-saga's
        // middleware calls next(action) before waking takeEvery watchers), so this is
        // just mirroring the already-updated list into localStorage, not recomputing it.
        const list = yield select((state) => state.notifications?.data?.notifications?.list ?? []);
        savePersistedNotifications(list);
        return;
    }

    const user = yield* currentUser();
    if (!user?.access_token) {
        return;
    }

    const result = yield call(Api.markNotificationRead, user, action.id);
    if (result?.status !== 0) {
        console.error("markNotificationReadCall: failed to sync read state", result);
        // The reducer already applied this optimistically - resync with backend truth
        // rather than leaving the client and server silently diverged. Dispatching the
        // action (instead of calling fetchNotificationsCall directly) routes this through
        // the same takeLatest watcher as every other trigger, so a burst of failures
        // collapses into a single effective refetch and still gets the race-safety above.
        yield put(fetchNotifications());
    }
}

export function* clearNotificationCall(action) {
    if (!isPersistenceFlagOn()) {
        const list = yield select((state) => state.notifications?.data?.notifications?.list ?? []);
        savePersistedNotifications(list);
        return;
    }

    const user = yield* currentUser();
    if (!user?.access_token) {
        return;
    }

    const result = yield call(Api.deleteNotification, user, action.id);
    if (result?.status !== 0) {
        console.error("clearNotificationCall: failed to sync delete", result);
        yield put(fetchNotifications());
    }
}

export function* clearAllNotificationsCall() {
    if (!isPersistenceFlagOn()) {
        const list = yield select((state) => state.notifications?.data?.notifications?.list ?? []);
        savePersistedNotifications(list);
        return;
    }

    const user = yield* currentUser();
    if (!user?.access_token) {
        return;
    }

    const result = yield call(Api.clearAllNotifications, user);
    if (result?.status !== 0) {
        console.error("clearAllNotificationsCall: failed to sync clear-all", result);
        yield put(fetchNotifications());
    }
}

// ADD_NOTIFICATION is dispatched for a live socket push (see notificationMiddleware.jsx).
// Flagged-off users have no backend copy of it, so mirror the reducer's post-append list
// into localStorage here too, or a live notification would vanish on the next refresh.
export function* addNotificationCall() {
    if (!isPersistenceFlagOn()) {
        const list = yield select((state) => state.notifications?.data?.notifications?.list ?? []);
        savePersistedNotifications(list);
        return;
    }

    // Flag on: the realtime-backend already wrote this notification to DynamoDB itself
    // when it pushed the socket event, so there's nothing left for the client to sync.
}

export function* watchNotificationSync() {
    // Both trigger the same fetch: DONE_LOGIN covers a fresh login submit, while
    // FETCH_NOTIFICATIONS (dispatched once at app boot, src/app.jsx) covers a plain
    // page refresh with an already-valid session, where PrivateRoute skips dispatching
    // DONE_LOGIN entirely (see src/routes.jsx) - without this second trigger, history
    // fetched via DONE_LOGIN's first-ever login would simply never re-fetch on reload.
    yield takeLatest(['DONE_LOGIN', 'FETCH_NOTIFICATIONS'], fetchNotificationsCall);
    yield takeLatest('FETCH_MORE_NOTIFICATIONS', fetchMoreNotificationsCall);
    yield takeEvery('MARK_NOTIFICATION_READ', markNotificationReadCall);
    yield takeEvery('CLEAR_NOTIFICATION', clearNotificationCall);
    yield takeLatest('CLEAR_ALL_NOTIFICATIONS', clearAllNotificationsCall);
    yield takeEvery('ADD_NOTIFICATION', addNotificationCall);
}
